import { ActiveUserTournament, LocalTournament } from 'react-easyrocket';
import { tournamentsStorageApi } from 'src/modules/localTournamentNotification/storageApi';
import { DELAY_TO_SHOW } from 'src/modules/localTournamentNotification/storageApi/constants';

type TournamentId = LocalTournament['id'];

const getSortedTournaments = (activeTournaments: LocalTournament[]): LocalTournament[] =>
  [...activeTournaments].sort((a, b) => b.dateStart - a.dateStart);

const getParticipatingIds = (
  activeLocalUserTournaments: ActiveUserTournament[] | undefined
): Set<TournamentId> => new Set(activeLocalUserTournaments?.map((t) => t.id) ?? []);

const findEligibleTournament = (
  sortedTournaments: LocalTournament[],
  lastShownId = sortedTournaments.length - 1,
  participatingIds: Set<TournamentId>,
  now: number
): LocalTournament | undefined => {
  const history = tournamentsStorageApi.getTournamentHistory();

  if (!lastShownId) {
    return sortedTournaments.find(({ id }) => !participatingIds.has(id));
  }

  const item = history[lastShownId] || { lastShown: 0, isClosed: false };

  if (!item.isClosed && item.lastShown + DELAY_TO_SHOW > now) {
    return sortedTournaments.find(({ id }) => !participatingIds.has(id) && id === lastShownId);
  }

  if (item.isClosed && item.lastShown + DELAY_TO_SHOW > now) {
    return undefined;
  }

  if (item.lastShown + DELAY_TO_SHOW < now) {
    const lastShowIndex = sortedTournaments.findIndex(({ id }) => id === lastShownId);
    return getNextTournamentIndex(sortedTournaments, lastShowIndex, participatingIds);
  }
};

export const getNextLocalTournament = (
  activeTournaments: LocalTournament[] | undefined,
  activeLocalUserTournaments: ActiveUserTournament[] | undefined,
  now: number = Date.now()
): LocalTournament | undefined => {
  if (!activeTournaments || activeTournaments.length === 0) return undefined;

  const sortedTournaments = getSortedTournaments(activeTournaments);
  const participatingIds = getParticipatingIds(activeLocalUserTournaments);
  const lastShownId = tournamentsStorageApi.getLastShownId() as number | undefined;

  const eligibleTournament = findEligibleTournament(
    sortedTournaments,
    lastShownId,
    participatingIds,
    now
  );

  return eligibleTournament;
};

function getNextTournamentIndex(
  tournaments: LocalTournament[],
  lastShowIndex: number,
  participatingIds: Set<number>
) {
  let i = lastShowIndex + 1 >= tournaments.length ? 0 : lastShowIndex + 1;
  let j = 0;
  while (j <= tournaments.length) {
    if (i + 1 >= tournaments.length) {
      i = 0;
    }
    if (!participatingIds.has(tournaments[i].id)) return tournaments[i];
    i++;
    j++;
  }
  return undefined;
}
